import { useState, useCallback } from 'react';

/**
 * Custom hook for standardized toast notifications
 * @returns {Object} toast state and functions
 * 
 * Usage:
 * const toast = useToast();
 * toast.success('City added successfully!');
 * toast.error('Failed to add city');
 * toast.info('Processing...');
 * toast.warning('Are you sure?');
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    
    setToasts(prev => [...prev, { id, message, type, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const success = useCallback((message) => {
    return showToast(message, 'success', 3000);
  }, [showToast]);

  const error = useCallback((message) => {
    return showToast(message, 'error', 4000);
  }, [showToast]);

  const warning = useCallback((message) => {
    return showToast(message, 'warning', 3500);
  }, [showToast]);

  const info = useCallback((message) => {
    return showToast(message, 'info', 3000);
  }, [showToast]);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return {
    toasts,
    showToast,
    success,
    error,
    warning,
    info,
    remove,
  };
};
